// Phase 6.0 — Region-aware stock helpers for the customer dashboard.
//
// Background: Phase 5.9.4 / Phase 7 moved real inventory into
// `product.region_stock[<region>]`. The legacy global `product.stock` field
// is kept around for back-compat but is frequently 0 for products that have
// per-region inventory. Any code path that gates visibility on `p.stock > 0`
// will incorrectly hide products that ARE in stock for the caller's region.
//
// Use these helpers for any customer-side stock check.

/**
 * Effective stock for a customer in a given region.
 *
 * Resolution order:
 *   1. If `region_stock` exists with any entries, return `region_stock[region] ?? 0`
 *      (treat missing-key-for-this-region as 0, NOT as fall-through to legacy).
 *   2. Otherwise (truly legacy products with no `region_stock` at all), fall
 *      back to global `product.stock ?? 0`. As of Phase 6.0 EVERY product in
 *      the catalog has `region_stock` populated, so this branch is effectively
 *      dead — kept as a defensive guard for any future fresh-seed product
 *      that hasn't yet been region-stocked. Server side also enforces this
 *      filter (see /api/products), so leaking is double-guarded.
 *
 * @param {object} product
 * @param {string|undefined} region - e.g. 'dmv' / 'nj'. If falsy, returns
 *   `product.stock` as best-effort guess.
 * @returns {number}
 */
export function customerEffectiveStock(product, region) {
  if (!product) return 0;
  const rs = product.region_stock;
  const hasRegionStock = rs && typeof rs === 'object' && Object.keys(rs).length > 0;
  if (hasRegionStock) {
    if (!region) {
      // No region known — fall back to sum across regions so we don't
      // under-count when ageGated/anonymous catalog peeks happen.
      return Object.values(rs).reduce((acc, v) => acc + (Number(v) || 0), 0);
    }
    return Number(rs[region] ?? 0);
  }
  return Number(product.stock ?? 0);
}

/** Sold out for the customer: server flag (switch on) or no stock. */
export function isSoldOut(product, region) {
  return !!product?.sold_out || customerEffectiveStock(product, region) <= 0;
}

/** Sold out products go to the end of the menu, original order otherwise kept. */
export function soldOutLast(list, region) {
  return [...list.filter((p) => !isSoldOut(p, region)), ...list.filter((p) => isSoldOut(p, region))];
}

/** Convenience boolean: is this product orderable for the given region? */
export function customerInStock(product, region) {
  return customerEffectiveStock(product, region) > 0;
}
